import React, { useState, useEffect } from 'react';
import styled from 'styled-components';
import { FiSave, FiArrowLeft, FiLoader, FiMail, FiBriefcase, FiTarget, FiTrash2, FiMapPin, FiMessageSquare } from 'react-icons/fi';
import { useNavigate, useParams } from 'react-router-dom';
import { leadsAPI, ibgeAPI } from '../services/api';
import { ContatosList } from '../components/leads';
import theme from '../styles/theme';
import Layout from '../components/Layout';

const Container = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
  max-width: 800px;
  margin: 0 auto;
`;

const Header = styled.div`
  display: flex;
  align-items: center;
  gap: ${theme.spacing[4]};
  margin-bottom: ${theme.spacing[4]};
`;

const BackButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  background: transparent;
  border: 1px solid ${theme.colors.border.primary};
  color: ${theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${theme.colors.background.primary};
    color: ${theme.colors.primary[500]};
    border-color: ${theme.colors.primary[500]};
  }
`;

const Title = styled.h1`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes['2xl']};
  font-weight: 700;
  margin: 0;
`;

const Form = styled.form`
  background: ${theme.colors.background.secondary};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[8]};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[6]};
`;

const FormSection = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[4]};
`;

const SectionTitle = styled.h3`
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.lg};
  font-weight: 600;
  margin: 0;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
`;

const FormGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
  gap: ${theme.spacing[4]};
`;

const FormGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing[2]};
`;

const FormGroupFull = styled(FormGroup)`
  grid-column: 1 / -1;
`;

const Label = styled.label`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.sm};
  font-weight: 600;
`;

const Input = styled.input`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &::placeholder {
    color: ${theme.colors.text.muted};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Select = styled.select`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  cursor: pointer;
  transition: all 0.2s ease;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }

  option {
    background: ${theme.colors.background.primary};
    color: ${theme.colors.text.primary};
  }
`;

const TextArea = styled.textarea`
  padding: ${theme.spacing[3]} ${theme.spacing[4]};
  border: 1px solid ${theme.colors.border.primary};
  border-radius: ${theme.borderRadius.lg};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  font-size: ${theme.fontSizes.base};
  transition: all 0.2s ease;
  resize: vertical;
  min-height: 100px;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
    box-shadow: 0 0 0 3px ${theme.colors.primary[500]}20;
  }

  &::placeholder {
    color: ${theme.colors.text.muted};
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const Actions = styled.div`
  display: flex;
  gap: ${theme.spacing[4]};
  justify-content: flex-end;
  padding-top: ${theme.spacing[6]};
  border-top: 1px solid ${theme.colors.border.primary};
`;

const Button = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  border: 1px solid ${props => {
    if (props.$danger) return theme.colors.error[500];
    if (props.$primary) return theme.colors.primary[500];
    return theme.colors.border.primary;
  }};
  background: ${props => {
    if (props.$danger) return theme.colors.error[500];
    if (props.$primary) return theme.colors.primary[500];
    return 'transparent';
  }};
  color: ${props => {
    if (props.$danger || props.$primary) return theme.colors.text.primary;
    return theme.colors.text.secondary;
  }};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};
  min-width: 120px;
  justify-content: center;

  &:hover:not(:disabled) {
    background: ${props => {
      if (props.$danger) return theme.colors.error[600];
      if (props.$primary) return theme.colors.primary[600];
      return theme.colors.background.primary;
    }};
    color: ${props => {
      if (props.$danger || props.$primary) return theme.colors.text.primary;
      return theme.colors.primary[500];
    }};
    border-color: ${props => {
      if (props.$danger) return theme.colors.error[600];
      if (props.$primary) return theme.colors.primary[600];
      return theme.colors.primary[500];
    }};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

const ButtonIcon = styled.div`
  font-size: ${theme.fontSizes.base};
`;

const ErrorMessage = styled.div`
  background: ${theme.colors.error[500]}20;
  border: 1px solid ${theme.colors.error[500]};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  color: ${theme.colors.error[600]};
  font-size: ${theme.fontSizes.sm};
`;

const SuccessMessage = styled.div`
  background: ${theme.colors.success[500]}20;
  border: 1px solid ${theme.colors.success[500]};
  border-radius: ${theme.borderRadius.lg};
  padding: ${theme.spacing[4]};
  color: ${theme.colors.success[600]};
  font-size: ${theme.fontSizes.sm};
`;

// Modal de confirmação
const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
`;

const ModalContent = styled.div`
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.xl};
  padding: ${theme.spacing[8]};
  max-width: 500px;
  width: 90%;
  box-shadow: 0 20px 40px rgba(0, 0, 0, 0.3);
`;

const ModalTitle = styled.h3`
  color: ${theme.colors.error[500]};
  font-size: ${theme.fontSizes.xl};
  font-weight: 700;
  margin: 0 0 ${theme.spacing[4]} 0;
`;

const ModalMessage = styled.p`
  color: ${theme.colors.text.secondary};
  font-size: ${theme.fontSizes.base};
  line-height: 1.5;
  margin: 0 0 ${theme.spacing[6]} 0;
`;

const ModalActions = styled.div`
  display: flex;
  gap: ${theme.spacing[3]};
  justify-content: flex-end;
`;

const ModalButton = styled.button`
  padding: ${theme.spacing[3]} ${theme.spacing[6]};
  border: 1px solid ${props => props.$danger ? theme.colors.error[500] : theme.colors.border.primary};
  background: ${props => props.$danger ? theme.colors.error[500] : 'transparent'};
  color: ${props => props.$danger ? theme.colors.text.primary : theme.colors.text.secondary};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.fontSizes.base};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.2s ease;
  display: flex;
  align-items: center;
  gap: ${theme.spacing[2]};

  &:hover {
    background: ${props => props.$danger ? theme.colors.error[600] : theme.colors.background.secondary};
  }

  &:disabled {
    opacity: 0.5;
    cursor: not-allowed;
  }
`;

// Lista de segmentos disponíveis
const SEGMENTOS_OPTIONS = [
  { value: 'Revenda Veículos', label: 'Revenda Veículos' },
  { value: 'Concessionária', label: 'Concessionária' },
  { value: 'Loja de Motos', label: 'Loja de Motos' },
  { value: 'Locadora', label: 'Locadora' },
  { value: 'Oficina Mecânica', label: 'Oficina Mecânica' },
  { value: 'Funilaria', label: 'Funilaria' },
  { value: 'Autopeças', label: 'Autopeças' },
  { value: 'Despachante', label: 'Despachante' },
  { value: 'Seguradora', label: 'Seguradora' },
  { value: 'Financeira', label: 'Financeira' },
  { value: 'Outro', label: 'Outro' },
];

const LeadForm = () => {
  const navigate = useNavigate();
  const { id } = useParams();
  const isEditing = id && id !== 'novo';

  const [formData, setFormData] = useState({
    email: '',
    empresa: '',
    segmento: '',
    cidade: '',
    estado: '',
    origem_id: '',
    funil_id: '',
    etapa_atual_id: '',
    observacoes: ''
  });

  const [contatos, setContatos] = useState([{ nome: '', telefone: '' }]);

  const [funisList, setFunisList] = useState([]);
  const [etapasList, setEtapasList] = useState([]);
  const [origensList, setOrigensList] = useState([]);
  const [estadosList, setEstadosList] = useState([]);
  const [cidadesList, setCidadesList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingData, setLoadingData] = useState(isEditing);
  const [loadingEtapas, setLoadingEtapas] = useState(false);
  const [loadingCidades, setLoadingCidades] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  // Carregar dados do lead se estiver editando
  useEffect(() => {
    if (isEditing) {
      carregarLead();
    }
  }, [id]);

  // Carregar dados auxiliares
  useEffect(() => {
    carregarDadosAuxiliares();
    carregarEstados();
  }, []);

  // Carregar etapas quando o funil mudar
  useEffect(() => {
    if (formData.funil_id) {
      carregarEtapas(formData.funil_id);
    } else {
      setEtapasList([]);
    }
  }, [formData.funil_id]);

  // Carregar cidades quando o estado mudar
  useEffect(() => {
    if (formData.estado) {
      carregarCidades(formData.estado);
    } else {
      setCidadesList([]);
    }
  }, [formData.estado]);

  const carregarLead = async () => {
    try {
      setLoadingData(true);
      const response = await leadsAPI.buscarPorId(id);
      
      if (response.data.success) {
        const lead = response.data.data;
        setFormData({
          email: lead.email || '',
          empresa: lead.empresa || '',
          segmento: lead.segmento || '',
          cidade: lead.cidade || '',
          estado: lead.estado || '',
          origem_id: lead.origem_id || '',
          funil_id: lead.funil_id || '',
          etapa_atual_id: lead.etapa_atual_id || '',
          observacoes: lead.observacoes || ''
        });

        // Carregar contatos
        if (lead.contatos && lead.contatos.length > 0) {
          setContatos(lead.contatos.map(c => ({
            nome: c.nome || '',
            telefone: c.telefone || ''
          })));
        } else {
          setContatos([{ nome: '', telefone: '' }]);
        }
      } else {
        setError('Erro ao carregar dados do lead');
      }
    } catch (err) {
      console.error('Erro ao carregar lead:', err);
      setError('Erro ao conectar com o servidor');
    } finally {
      setLoadingData(false);
    }
  };

  const carregarDadosAuxiliares = async () => {
    try {
      const [funisResponse, origensResponse] = await Promise.all([
        leadsAPI.listarFunis(),
        leadsAPI.listarOrigens()
      ]);

      if (funisResponse.data.success) {
        setFunisList(funisResponse.data.data);
      }
      if (origensResponse.data.success) {
        setOrigensList(origensResponse.data.data);
      }
    } catch (err) {
      console.error('Erro ao carregar dados auxiliares:', err);
    }
  };

  const carregarEstados = async () => {
    try {
      const response = await ibgeAPI.listarEstados();
      const estadosOrdenados = response.data.sort((a, b) => a.nome.localeCompare(b.nome));
      setEstadosList(estadosOrdenados);
    } catch (err) {
      console.error('Erro ao carregar estados:', err);
    }
  };

  const carregarCidades = async (siglaEstado) => {
    try {
      setLoadingCidades(true);
      const response = await ibgeAPI.listarMunicipios(siglaEstado);
      const cidadesOrdenadas = response.data.sort((a, b) => a.nome.localeCompare(b.nome));
      setCidadesList(cidadesOrdenadas);
    } catch (err) {
      console.error('Erro ao carregar cidades:', err);
    } finally {
      setLoadingCidades(false);
    }
  };

  const carregarEtapas = async (funilId) => {
    try {
      setLoadingEtapas(true);
      const response = await leadsAPI.listarEtapas(funilId);
      
      if (response.data.success) {
        setEtapasList(response.data.data);
      }
    } catch (err) {
      console.error('Erro ao carregar etapas:', err);
    } finally {
      setLoadingEtapas(false);
    }
  };

  const handleInputChange = (field, value) => {
    setFormData(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const handleFunilChange = (value) => {
    setFormData(prev => ({
      ...prev,
      funil_id: value,
      etapa_atual_id: '' // Limpar etapa ao mudar funil
    }));
  };

  const handleEstadoChange = (value) => {
    setFormData(prev => ({
      ...prev,
      estado: value,
      cidade: '' // Limpar cidade ao mudar estado
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      setLoading(true);
      setError(null);
      setSuccess(null);

      // Validações básicas
      if (!formData.empresa && (!contatos || contatos.length === 0)) {
        throw new Error('Empresa ou pelo menos um contato é obrigatório');
      }

      // Validar contatos
      const contatosValidos = contatos.filter(c => c.nome || c.telefone);
      if (contatosValidos.length === 0) {
        throw new Error('Pelo menos um contato com nome ou telefone é obrigatório');
      }

      const dadosParaEnvio = {
        email: formData.email?.trim() || null,
        empresa: formData.empresa?.trim() || null,
        segmento: formData.segmento || null,
        cidade: formData.cidade?.trim() || null,
        estado: formData.estado || null,
        origem_id: formData.origem_id || null,
        funil_id: formData.funil_id || null,
        etapa_atual_id: formData.etapa_atual_id || null,
        observacoes: formData.observacoes?.trim() || null,
        contatos: contatosValidos.map(c => ({
          nome: c.nome?.trim() || null,
          telefone: c.telefone?.trim() || null
        }))
      };

      let response;
      if (isEditing) {
        response = await leadsAPI.atualizar(id, dadosParaEnvio);
      } else {
        response = await leadsAPI.criar(dadosParaEnvio);
      }

      if (response.data.success) {
        setSuccess(isEditing ? 'Lead atualizado com sucesso!' : 'Lead cadastrado com sucesso!');
        setTimeout(() => {
          navigate('/leads');
        }, 1500);
      } else {
        setError(response.data.error || 'Erro ao salvar lead');
      }
    } catch (err) {
      console.error('Erro ao salvar lead:', err);
      
      if (err.response?.data?.error) {
        setError(err.response.data.error);
      } else if (err.message) {
        setError(err.message);
      } else {
        setError('Erro ao conectar com o servidor. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  const handleVoltar = () => {
    navigate('/leads');
  };

  const handleExcluirLead = async () => {
    try {
      setDeleting(true);
      setError(null);

      await leadsAPI.deletar(id);
      
      navigate('/leads');
    } catch (err) {
      console.error('Erro ao excluir lead:', err);
      setError('Erro ao excluir lead');
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleAbrirModalExclusao = () => {
    setShowDeleteModal(true);
  };

  const handleCancelarExclusao = () => {
    setShowDeleteModal(false);
  };

  if (loadingData) {
    return (
      <Layout title="Carregando...">
        <Container>
          <div style={{ display: 'flex', justifyContent: 'center', padding: '4rem' }}>
            <FiLoader style={{ fontSize: '2rem', animation: 'spin 1s linear infinite' }} />
          </div>
        </Container>
      </Layout>
    );
  }

  return (
    <Layout title={isEditing ? 'Editar Lead' : 'Novo Lead'}>
      <Container>
        <Header>
          <BackButton onClick={handleVoltar}>
            <FiArrowLeft />
            Voltar
          </BackButton>
          <Title>
            {isEditing ? 'Editar Lead' : 'Novo Lead'}
          </Title>
        </Header>

        <Form onSubmit={handleSubmit}>
          {error && <ErrorMessage>{error}</ErrorMessage>}
          {success && <SuccessMessage>{success}</SuccessMessage>}

          <FormSection>
            <SectionTitle>
              <FiBriefcase />
              Informações da Empresa
            </SectionTitle>
            
            <FormGrid>
              <FormGroup>
                <Label htmlFor="empresa">Empresa</Label>
                <Input
                  id="empresa"
                  type="text"
                  placeholder="Nome da empresa"
                  value={formData.empresa}
                  onChange={(e) => handleInputChange('empresa', e.target.value)}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="email">
                  <FiMail style={{ marginRight: '4px' }} />
                  Email
                </Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="email@exemplo.com"
                  value={formData.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                />
              </FormGroup>

              <FormGroup>
                <Label htmlFor="segmento">
                  <FiTarget style={{ marginRight: '4px' }} />
                  Segmento
                </Label>
                <Select
                  id="segmento"
                  value={formData.segmento}
                  onChange={(e) => handleInputChange('segmento', e.target.value)}
                >
                  <option value="">Selecione o segmento</option>
                  {SEGMENTOS_OPTIONS.map((segmento) => (
                    <option key={segmento.value} value={segmento.value}>
                      {segmento.label}
                    </option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="origem">
                  <FiMapPin style={{ marginRight: '4px' }} />
                  Origem
                </Label>
                <Select
                  id="origem"
                  value={formData.origem_id}
                  onChange={(e) => handleInputChange('origem_id', e.target.value)}
                >
                  <option value="">Selecione a origem</option>
                  {origensList.map((origem) => (
                    <option key={origem.id} value={origem.id}>
                      {origem.nome}
                    </option>
                  ))}
                </Select>
              </FormGroup>
            </FormGrid>
          </FormSection>

          <FormSection>
            <ContatosList contatos={contatos} onChange={setContatos} />
          </FormSection>

          <FormSection>
            <SectionTitle>
              <FiMapPin />
              Localização
            </SectionTitle>
            
            <FormGrid>
              <FormGroup>
                <Label htmlFor="estado">Estado</Label>
                <Select
                  id="estado"
                  value={formData.estado}
                  onChange={(e) => handleEstadoChange(e.target.value)}
                >
                  <option value="">Selecione o estado</option>
                  {estadosList.map((estado) => (
                    <option key={estado.sigla} value={estado.sigla}>
                      {estado.nome}
                    </option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="cidade">Cidade</Label>
                <Select
                  id="cidade"
                  value={formData.cidade}
                  onChange={(e) => handleInputChange('cidade', e.target.value)}
                  disabled={!formData.estado || loadingCidades}
                >
                  <option value="">
                    {loadingCidades ? 'Carregando...' : 'Selecione a cidade'}
                  </option>
                  {cidadesList.map((cidade) => (
                    <option key={cidade.id} value={cidade.nome}>
                      {cidade.nome}
                    </option>
                  ))}
                </Select>
              </FormGroup>
            </FormGrid>
          </FormSection>

          <FormSection>
            <SectionTitle>
              <FiTarget />
              Prospecção
            </SectionTitle>
            
            <FormGrid>
              <FormGroup>
                <Label htmlFor="funil">Funil</Label>
                <Select
                  id="funil"
                  value={formData.funil_id}
                  onChange={(e) => handleFunilChange(e.target.value)}
                >
                  <option value="">Selecione o funil</option>
                  {funisList.map((funil) => (
                    <option key={funil.id} value={funil.id}>
                      {funil.nome}
                    </option>
                  ))}
                </Select>
              </FormGroup>

              <FormGroup>
                <Label htmlFor="etapa">Etapa</Label>
                <Select
                  id="etapa"
                  value={formData.etapa_atual_id}
                  onChange={(e) => handleInputChange('etapa_atual_id', e.target.value)}
                  disabled={!formData.funil_id || loadingEtapas}
                >
                  <option value="">
                    {loadingEtapas ? 'Carregando...' : 'Selecione a etapa'}
                  </option>
                  {etapasList.map((etapa) => (
                    <option key={etapa.id} value={etapa.id}>
                      {etapa.ordem}. {etapa.nome}
                    </option>
                  ))}
                </Select>
              </FormGroup>
            </FormGrid>
          </FormSection>

          <FormSection>
            <SectionTitle>
              <FiMessageSquare />
              Observações
            </SectionTitle>
            
            <FormGroupFull>
              <TextArea
                id="observacoes"
                placeholder="Informações adicionais sobre o lead..."
                value={formData.observacoes}
                onChange={(e) => handleInputChange('observacoes', e.target.value)}
              />
            </FormGroupFull>
          </FormSection>

          <Actions>
            <Button type="button" onClick={handleVoltar}>
              Cancelar
            </Button>
            {isEditing && (
              <Button type="button" onClick={handleAbrirModalExclusao} $danger>
                <ButtonIcon>
                  <FiTrash2 />
                </ButtonIcon>
                Excluir
              </Button>
            )}
            <Button $primary type="submit" disabled={loading}>
              <ButtonIcon>
                {loading ? <FiLoader /> : <FiSave />}
              </ButtonIcon>
              {loading ? 'Salvando...' : 'Salvar'}
            </Button>
          </Actions>
        </Form>
      </Container>

      {/* Modal de confirmação de exclusão */}
      {showDeleteModal && (
        <ModalOverlay>
          <ModalContent>
            <ModalTitle>Excluir Lead</ModalTitle>
            <ModalMessage>
              Tem certeza que deseja excluir este lead? Esta ação não pode ser desfeita e irá excluir:
              <br /><br />
              • O lead <strong>{formData.empresa || 'Sem empresa'}</strong>
              <br />
              • Todos os contatos relacionados
              <br />
              • Todas as interações relacionadas
              <br />
              • Todos os registros de prospecção
            </ModalMessage>
            <ModalActions>
              <ModalButton onClick={handleCancelarExclusao}>
                Cancelar
              </ModalButton>
              <ModalButton $danger onClick={handleExcluirLead} disabled={deleting}>
                <FiTrash2 />
                {deleting ? 'Excluindo...' : 'Excluir'}
              </ModalButton>
            </ModalActions>
          </ModalContent>
        </ModalOverlay>
      )}
    </Layout>
  );
};

export default LeadForm;
